import ApolloClient from 'apollo-client'
import { resolveValue } from 'components/form/final-form/hooks/useField'
import { Calculation } from 'final-form-calculate'
import { SituacaoProblema } from 'graphql/types.generated'
import { isUndefinedOrNull } from 'util/checks'
import { MetaPath } from 'util/metaPath'
import { CiapCidPreNatal, SoapState } from 'view/atendimentos/atendimento-individual/model'
import { RegistroAvaliacaoPuericultura } from 'view/atendimentos/model-atendimento'

import { Problema } from '../aside/types/ProblemaModel'
import { ObjetivoFormModel } from '../objetivo'
import { FormAtivoObjetivoEnum } from '../objetivo/components/SwitchButtonObjetivoForm'
import { createPreNatalAvaliacaoCalculations } from '../pre-natal/calculator-preNatal'
import { CidPreNatalModel } from '../pre-natal/model-preNatal'
import { getCidPrimeiroAtendimentoPreNatal } from '../pre-natal/util-preNatal'
import { AvaliacaoFormModel } from './AvaliacaoForm'
import { ProblemaCondicaoModel } from './components/problemas-condicoes/model-problemasCondicoes'
import {
  addProblemaCondicaoAutomatico,
  createProblemaCondicaoAutomatico,
  createProblemaCondicaoAutomaticoPreNatal,
  findProblemaByCiapIdCidId,
  removeProblemaCondicao,
  removeProblemasCondicoesDePreNatal,
} from './components/problemas-condicoes/utils/operations-problemasCondicoes'
import { hasProblemaCondicaoDePreNatal } from './components/problemas-condicoes/utils/verifications-preNatal'

export const createAvaliacaoCalculations = (
  objetivo: MetaPath<ObjetivoFormModel>,
  avaliacao: MetaPath<AvaliacaoFormModel>,
  problemaCondicaoPuericultura: RegistroAvaliacaoPuericultura,
  isMedico: boolean,
  apollo: ApolloClient<object>,
  prontuarioId: ID,
  ciapCidPreNatal: CiapCidPreNatal,
  dataNascimentoCidadao: LocalDate,
  dataAtendimento: Instant,
  hasPermissionPreNatal: boolean,
  isContinuacaoPreNatal: boolean,
  hasProblemaComCiapW78AtivoPersistido: boolean,
  problemasCidadao?: Problema[]
): Calculation[] => [
  ...createPreNatalAvaliacaoCalculations(
    avaliacao,
    isMedico,
    dataNascimentoCidadao,
    dataAtendimento,
    ciapCidPreNatal,
    isContinuacaoPreNatal,
    hasPermissionPreNatal,
    problemasCidadao
  ),
  {
    field: objetivo.atendimentosEspecificos.formAtivo.absolutePath(),
    updates: {
      [avaliacao.problemasECondicoes.absolutePath()]: async (
        formAtivo: FormAtivoObjetivoEnum,
        allValues: SoapState,
        prevValues: SoapState
      ) => {
        let listaProblemasCondicoes =
          resolveValue<ProblemaCondicaoModel[]>(allValues, avaliacao.problemasECondicoes) ?? []
        const formEstavaAtivo = resolveValue<FormAtivoObjetivoEnum>(
          prevValues,
          objetivo.atendimentosEspecificos.formAtivo
        )

        let cidPrimeiroAtendimentoPreNatal = null
        if (formAtivo === FormAtivoObjetivoEnum.PRE_NATAL || formEstavaAtivo === FormAtivoObjetivoEnum.PRE_NATAL)
          cidPrimeiroAtendimentoPreNatal = await getCidPrimeiroAtendimentoPreNatal(prontuarioId, apollo)

        if (!isUndefinedOrNull(formAtivo)) {
          listaProblemasCondicoes = handleFormAtivo(
            formAtivo,
            listaProblemasCondicoes,
            problemaCondicaoPuericultura,
            isMedico,
            cidPrimeiroAtendimentoPreNatal,
            ciapCidPreNatal,
            dataNascimentoCidadao,
            hasProblemaComCiapW78AtivoPersistido,
            problemasCidadao
          )
        }

        if (!isUndefinedOrNull(formEstavaAtivo)) {
          listaProblemasCondicoes = handleFormEstavaAtivo(
            formEstavaAtivo,
            isMedico,
            problemaCondicaoPuericultura,
            listaProblemasCondicoes,
            hasProblemaComCiapW78AtivoPersistido
          )
        }

        return listaProblemasCondicoes
      },
    },
  },
]

const handleFormEstavaAtivo = (
  formEstavaAtivo: FormAtivoObjetivoEnum,
  isMedico: boolean,
  problemaCondicaoPuericultura: RegistroAvaliacaoPuericultura,
  listaProblemasCondicoes: ProblemaCondicaoModel[],
  hasProblemaComCiapW78AtivoPersistido: boolean
): ProblemaCondicaoModel[] => {
  switch (formEstavaAtivo) {
    case FormAtivoObjetivoEnum.PUERICULTURA: {
      return removeProblemaCondicao(listaProblemasCondicoes, isMedico, {
        ciapToRemove: problemaCondicaoPuericultura.ciap?.codigo,
        cidToRemove: problemaCondicaoPuericultura.cid10?.codigo,
      })
    }
    case FormAtivoObjetivoEnum.PRE_NATAL: {
      return removeProblemasCondicoesDePreNatal(listaProblemasCondicoes, hasProblemaComCiapW78AtivoPersistido)
    }
    default:
      return listaProblemasCondicoes
  }
}

const handleFormAtivo = (
  formAtivo: FormAtivoObjetivoEnum,
  listaProblemasCondicoes: ProblemaCondicaoModel[],
  problemaCondicaoPuericultura: RegistroAvaliacaoPuericultura,
  isMedico: boolean,
  cidPrimeiroAtendimento: CidPreNatalModel,
  ciapCidPreNatal: CiapCidPreNatal,
  dataNascimentoCidadao: LocalDate,
  hasProblemaComCiapW78AtivoPersistido: boolean,
  problemasCidadao?: Problema[]
): ProblemaCondicaoModel[] => {
  switch (formAtivo) {
    case FormAtivoObjetivoEnum.PRE_NATAL: {
      if (hasProblemaCondicaoDePreNatal(listaProblemasCondicoes, hasProblemaComCiapW78AtivoPersistido)) {
        return listaProblemasCondicoes
      } else {
        const problemaPreNatalJaExistente = findProblemaByCiapIdCidId(
          problemasCidadao,
          ciapCidPreNatal.ciap?.id,
          cidPrimeiroAtendimento?.id
        )
        return addProblemaCondicaoAutomatico(
          listaProblemasCondicoes,
          createProblemaCondicaoAutomaticoPreNatal(
            ciapCidPreNatal,
            isMedico,
            SituacaoProblema.ATIVO,
            cidPrimeiroAtendimento,
            problemaPreNatalJaExistente,
            dataNascimentoCidadao
          )
        )
      }
    }
    case FormAtivoObjetivoEnum.PUERICULTURA: {
      return addProblemaCondicaoAutomatico(
        listaProblemasCondicoes,
        createProblemaCondicaoAutomaticoPuericultura(problemaCondicaoPuericultura, isMedico)
      )
    }
    default:
      return listaProblemasCondicoes
  }
}

const createProblemaCondicaoAutomaticoPuericultura = (
  problemaCondicaoPuericultura: RegistroAvaliacaoPuericultura,
  isMedico: boolean
): ProblemaCondicaoModel =>
  createProblemaCondicaoAutomatico(problemaCondicaoPuericultura.ciap, problemaCondicaoPuericultura.cid10, isMedico)
